/**
 * DesignMode — Brand panel (colors, fonts, radius) + content editing.
 * Ported from Builder-5 DesignMode.jsx with Zustand integration.
 *
 * Note: ColorStack, ColorPalette, FontPicker, RadiusPicker are placeholders
 * that will be implemented as full components in a later phase.
 */
import { useBuilderStore } from '@/state/store';
import { fontStacks } from '@/data/font-stacks';
import { FontPreviewSelect } from '@/components/editor/FontPreviewSelect';

export function DesignMode() {
  const sidebarTab = useBuilderStore((s) => s.sidebarTab);
  const settings = useBuilderStore((s) => s.settings);
  const updateSettings = useBuilderStore((s) => s.updateSettings);

  if (sidebarTab === 'content') {
    return (
      <div className="sidebar__section">
        <div className="sidebar__sectionHeader">
          <div className="sidebar__sectionTitle"><h3>Inhalt bearbeiten</h3></div>
        </div>
        <div className="sidebar__sectionBody">
          <div className="form-group">
            <label className="form-label">Site Name</label>
            <input
              type="text"
              className="form-input"
              value={settings.siteName || ''}
              onChange={(e) =>
                updateSettings({ ...settings, siteName: e.target.value })
              }
            />
          </div>
          <div className="form-group">
            <label className="form-label">Beschreibung</label>
            <textarea
              className="form-textarea"
              rows={3}
              value={settings.siteDescription || ''}
              onChange={(e) =>
                updateSettings({ ...settings, siteDescription: e.target.value })
              }
            />
          </div>
        </div>
      </div>
    );
  }

  // Brand tab
  return (
    <>
      <div className="sidebar__section">
        <div className="sidebar__sectionHeader">
          <div className="sidebar__sectionTitle"><h3>Farben</h3></div>
        </div>
        <div className="sidebar__sectionBody">
          <div className="form-group">
            <label className="form-label">Primärfarbe</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                type="color"
                value={settings.primaryColor || '#0f766e'}
                onChange={(e) =>
                  updateSettings({ ...settings, primaryColor: e.target.value })
                }
                style={{ width: 40, height: 32, border: 'none', cursor: 'pointer' }}
              />
              <input
                type="text"
                className="form-input"
                value={settings.primaryColor || '#0f766e'}
                onChange={(e) =>
                  updateSettings({ ...settings, primaryColor: e.target.value })
                }
                style={{ flex: 1 }}
              />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Akzentfarbe</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                type="color"
                value={settings.accentColor || '#d4a84b'}
                onChange={(e) =>
                  updateSettings({ ...settings, accentColor: e.target.value })
                }
                style={{ width: 40, height: 32, border: 'none', cursor: 'pointer' }}
              />
              <input
                type="text"
                className="form-input"
                value={settings.accentColor || '#d4a84b'}
                onChange={(e) =>
                  updateSettings({ ...settings, accentColor: e.target.value })
                }
                style={{ flex: 1 }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="sidebar__section">
        <div className="sidebar__sectionHeader">
          <div className="sidebar__sectionTitle"><h3>Schrift</h3></div>
        </div>
        <div className="sidebar__sectionBody">
          <div className="form-group">
            <label className="form-label">Font Stack</label>
            <FontPreviewSelect
              value={settings.fontStack || 'system'}
              onChange={(key) =>
                updateSettings({ ...settings, fontStack: key })
              }
            />
          </div>
        </div>
      </div>

      <div className="sidebar__section">
        <div className="sidebar__sectionHeader">
          <div className="sidebar__sectionTitle"><h3>Radius</h3></div>
        </div>
        <div className="sidebar__sectionBody">
          <div className="form-group">
            <label className="form-label">Border Radius</label>
            <select
              className="form-select"
              value={settings.borderRadius || 'rounded'}
              onChange={(e) =>
                updateSettings({ ...settings, borderRadius: e.target.value })
              }
            >
              <option value="none">Sharp (0px)</option>
              <option value="rounded">Rounded (8px)</option>
              <option value="pill">Pill (999px)</option>
            </select>
          </div>
        </div>
      </div>

      <div className="sidebar__section">
        <div className="sidebar__sectionHeader">
          <div className="sidebar__sectionTitle"><h3>Button-Ecken</h3></div>
        </div>
        <div className="sidebar__sectionBody">
          <div className="form-group">
            <label className="form-label">Button Radius</label>
            <select
              className="form-select"
              value={settings.buttonRadius || 'rounded'}
              onChange={(e) =>
                updateSettings({ ...settings, buttonRadius: e.target.value })
              }
            >
              <option value="none">Sharp (0px)</option>
              <option value="rounded">Rounded (8px)</option>
              <option value="pill">Pill (999px)</option>
            </select>
          </div>
        </div>
      </div>

      <div className="sidebar__section">
        <div className="sidebar__sectionHeader">
          <div className="sidebar__sectionTitle"><h3>Button-Stil</h3></div>
        </div>
        <div className="sidebar__sectionBody">
          <div className="form-group">
            <div className="btn-style-picker">
              {(['filled', 'outline', 'soft'] as const).map((style) => (
                <button
                  key={style}
                  type="button"
                  className={`btn-style-picker__option${
                    (settings.buttonStyle || 'filled') === style ? ' is-active' : ''
                  }`}
                  onClick={() =>
                    updateSettings({ ...settings, buttonStyle: style })
                  }
                >
                  <span
                    className={`btn-style-picker__preview btn-style-picker__preview--${style}`}
                    style={{
                      '--picker-primary': settings.primaryColor || '#0f766e',
                    } as React.CSSProperties}
                  >
                    Button
                  </span>
                  <span className="btn-style-picker__label">
                    {style === 'filled' ? 'Filled' : style === 'outline' ? 'Outline' : 'Soft'}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="sidebar__section">
        <div className="sidebar__sectionHeader">
          <div className="sidebar__sectionTitle"><h3>Dark Mode</h3></div>
        </div>
        <div className="sidebar__sectionBody">
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={settings.darkMode || false}
              onChange={() =>
                updateSettings({ ...settings, darkMode: !settings.darkMode })
              }
            />
            <span className="form-label" style={{ margin: 0 }}>
              Website im Dark Mode generieren
            </span>
          </label>
        </div>
      </div>
    </>
  );
}
